import { useEffect, useState } from 'react';

export const COOKIE_NAME = 'cys_cookie_consent';
export const CONSENT_EVENT = 'cys-cookie-consent-change';

export type ConsentCategories = {
  necessary?: boolean;
  preferences?: boolean;
  analytics?: boolean;
  marketing?: boolean;
  [key: string]: boolean | undefined;
};

export type ConsentDecision = 'accept_all' | 'reject_all' | 'custom' | 'withdraw';

/** Persisted cookie shape. */
export type StoredConsent = {
  decision: ConsentDecision;
  categories: ConsentCategories;
  policy_version_id: string;
  recorded_at: number;
};

/** Reduced shape consumed by `includeScripts.js` for analytics/marketing gating. */
export type ScriptGatingDecision = 'accepted' | 'rejected';

/** Read the raw cookie value. */
const readCookieRaw = (): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

/** Type guard for the persisted JSON shape. */
const isStoredConsentShape = (v: unknown): v is StoredConsent =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as StoredConsent).decision === 'string' &&
  typeof (v as StoredConsent).policy_version_id === 'string' &&
  !!(v as StoredConsent).categories &&
  typeof (v as StoredConsent).categories === 'object';

/** Parse the consent cookie. Null on missing/malformed/stale-shape. */
export const readStoredConsent = (): StoredConsent | null => {
  const raw = readCookieRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return isStoredConsentShape(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Reduce a decision to the boolean-ish flag the script-gating code reads:
 * `accept_all` → `'accepted'`; `reject_all`/`withdraw` → `'rejected'`;
 * `custom` → analytics flag wins.
 */
const reduceForScriptGating = (s: StoredConsent | null): ScriptGatingDecision | null => {
  if (s === null) return null;
  if (s.decision === 'accept_all') return 'accepted';
  if (s.decision === 'reject_all' || s.decision === 'withdraw') return 'rejected';
  return s.categories?.analytics ? 'accepted' : 'rejected';
};

/** Subscribe to cookie changes; project them through `reducer`. */
const useReducedConsent = <T>(reducer: (s: StoredConsent | null) => T, initial: T): T => {
  const [value, setValue] = useState<T>(initial);
  useEffect(() => {
    const update = () => setValue(reducer(readStoredConsent()));
    update();
    window.addEventListener(CONSENT_EVENT, update);
    return () => window.removeEventListener(CONSENT_EVENT, update);
  }, []);
  return value;
};

/** Hook returning `'accepted' | 'rejected' | null` for `includeScripts.js`. */
export const useCookieConsent = (): ScriptGatingDecision | null =>
  useReducedConsent(reduceForScriptGating, null);

/** Hook returning the full structured consent (or null). */
export const useStoredConsent = (): StoredConsent | null =>
  useReducedConsent(s => s, null);

/** Notify subscribers after writing/clearing the cookie. */
export const notifyConsentChange = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CONSENT_EVENT));
  }
};
